import { Visibility } from '@mui/icons-material';
import { AlertColor, Avatar, Box, Chip, FormControl, InputLabel, MenuItem, Select, TextField, Typography, useTheme } from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { filterDataforApi, pageNo, rowCount } from 'utils/redux/reducer/filter-slice';
import strings from '../../../common/Translation/Translate';
import Notification from '../../../components/Notification';
import PageLoader from '../../../components/PageLoader';
import axiosInstance from '../../../utils/axios';
import { PageProps } from '../../../utils/constants/data';
import { privileges } from '../../../utils/constants/privileges';
import { routes } from '../../../utils/constants/routes';
import { URLS } from '../../../utils/constants/urls';
import { createDropdownData } from '../../../utils/validators/HelperFunctions';
import { getTableAvatarSx } from '../../../utils/avatarColors';
import ListPage from '../CommonPage';
import RfqEntityDrawer from './RfqEntityDrawer';
import RfqStatusCards from './RfqStatusCards';
import { getRfqStatusColors } from './rfqStatusColors';

dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);

const stripFilterKey = (filter: unknown, key: string) => {
  const base = Array.isArray(filter)
    ? filter.filter((item: unknown) => typeof item !== 'string' || !item.startsWith(`${key}:`))
    : [];
  const cleaned: string[] = [];
  base.forEach((item: string) => {
    if (item === 'and') {
      if (cleaned.length === 0 || cleaned[cleaned.length - 1] === 'and') return;
      cleaned.push(item);
      return;
    }
    cleaned.push(item);
  });
  if (cleaned.length && cleaned[cleaned.length - 1] === 'and') cleaned.pop();
  return cleaned;
};

const withCicFilter = (filter: unknown, key: string, value: string) => {
  const cleaned = stripFilterKey(filter, key);
  if (!value) return cleaned;
  if (cleaned.length) cleaned.push('and');
  cleaned.push(`${key}:cic:${value}`);
  return cleaned;
};

const getInitials = (name?: string) => {
  if (!name?.trim()) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const formatDateLine = (value?: string | null) => {
  if (!value) return '—';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('D MMM YYYY') : '—';
};

const formatWeekdayTime = (date?: string | null, time?: string | null) => {
  if (!date) return '';
  const datePart = dayjs(date);
  if (!datePart.isValid()) return '';
  if (!time) return datePart.format('ddd');
  const combined = dayjs(`${datePart.format('YYYY-MM-DD')} ${time}`, 'YYYY-MM-DD HH:mm', true);
  if (combined.isValid()) return combined.format('ddd, h:mm A');
  const fallback = dayjs(time, ['HH:mm', 'h:mm A', 'HH:mm:ss'], true);
  return fallback.isValid() ? `${datePart.format('ddd')}, ${fallback.format('h:mm A')}` : datePart.format('ddd');
};

const formatRelativeReceived = (value?: string | null) => {
  if (!value) return '';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.fromNow() : '';
};

const DualLineCell: FC<{ primary: string; secondary?: string }> = ({ primary, secondary }) => (
  <Box sx={{ py: 0.25 }}>
    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.35, color: '#101828' }}>
      {primary || '—'}
    </Typography>
    {secondary ? (
      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.35 }}>{secondary}</Typography>
    ) : null}
  </Box>
);

const RfqList: FC = () => {
  const index = useSelector(pageNo);
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);
  const navigate = useNavigate();
  const theme = useTheme();

  const [pageResponse, setPageResponse] = useState<any[]>([]);
  const [rfqStatusOptions, setRfqStatusOptions] = useState<any>([]);
  const [rfqPointers, setRfqPointers] = useState<any>(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedMonth, setSelectedMonth] = useState<Dayjs | null>(null);
  const [addDrawerOpen, setAddDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [notification, setNotification] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const closeNotification = (value: boolean) => setNotification(prev => ({ ...prev, display: value }));

  const handleCustomImageMethod = useCallback((id: number) => navigate(`${routes.viewRfq}/${id}`), [navigate]);

  const withRfqFilters = useCallback(
    (baseFilter: unknown) => {
      let next = withCicFilter(baseFilter, 'status', selectedStatus);
      next = stripFilterKey(next, 'preferredStartDate');
      next = stripFilterKey(next, 'preferredStartDateTo');
      if (selectedMonth?.isValid()) {
        next = withCicFilter(next, 'preferredStartDate', selectedMonth.startOf('month').format('YYYY-MM-DD'));
        next = withCicFilter(next, 'preferredStartDateTo', selectedMonth.endOf('month').format('YYYY-MM-DD'));
      }
      return next;
    },
    [selectedMonth, selectedStatus],
  );

  const getChipStyles = useCallback(
    (status: string) => {
      const statusColors = getRfqStatusColors(status);
      if (statusColors) return statusColors;
      return { backgroundColor: theme.palette.secondary.light, color: theme.palette.primary.main };
    },
    [theme.palette.primary.main, theme.palette.secondary.light],
  );

  const pageConfig = useMemo(
    () => ({
      title: strings.rfq_title_list,
      subtitle: 'Track and convert incoming quote requests.',
      entityLabel: 'RFQs',
      searchField: 'search',
      searchPlaceholder: 'Search by name, venue, email, company, or mobile',
      editButtonUrl: routes.editCustomer,
      listApi: URLS.requestForQuotes,
      AddButtonText: 'Add RFQ',
      addPrivilege: privileges.add_rfq,
      customAdd: () => setAddDrawerOpen(true),
      exportPrivilege: privileges.export_rfq,
      exportIconOnly: true,
      exportApi: URLS.exportRfqs,
      deleteApi: URLS.deleteRfq,
      deletePrivilege: privileges.delete_rfq,
      renderKpis: () => (
        <RfqStatusCards
          totalLeads={rfqPointers?.total || 0}
          newLeads={rfqPointers?.newLeads || 0}
          inProgressLeads={rfqPointers?.inProgress || 0}
          convertedLeads={rfqPointers?.converted || 0}
        />
      ),
      headerFilters: () => (
        <>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel sx={{ fontSize: 12 }}>Status</InputLabel>
            <Select
              label="Status"
              value={selectedStatus}
              onChange={event => setSelectedStatus(String(event.target.value))}
              sx={{ height: 36, fontSize: 13, bgcolor: '#fff', '& .MuiSelect-select': { py: 0.75 } }}>
              <MenuItem value="">All statuses</MenuItem>
              {rfqStatusOptions.map((option: { label: string; value: string | number }) => (
                <MenuItem key={String(option.value)} value={String(option.value)} sx={{ fontSize: 13 }}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              views={['month', 'year']}
              label="Event month"
              inputFormat="MMM YYYY"
              value={selectedMonth}
              onChange={(value: Dayjs | null) => setSelectedMonth(value)}
              renderInput={params => (
                <TextField
                  {...params}
                  size="small"
                  sx={{
                    minWidth: 150,
                    bgcolor: '#fff',
                    '& .MuiInputBase-root': { height: 36, fontSize: 13 },
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </>
      ),
      filterChips: [
        ...(selectedStatus
          ? [
              {
                key: 'status',
                label: `Status : ${
                  rfqStatusOptions.find((o: { value: string | number }) => String(o.value) === selectedStatus)?.label ||
                  selectedStatus
                }`,
              },
            ]
          : []),
        ...(selectedMonth?.isValid()
          ? [{ key: 'preferredStartDate', label: `Event month : ${selectedMonth.format('MMM YYYY')}` }]
          : []),
      ],
      onClearFilterChip: (key: string) => {
        if (key === 'status') setSelectedStatus('');
        if (key === 'preferredStartDate') setSelectedMonth(null);
      },
      onClearAllFilterChips: () => {
        setSelectedStatus('');
        setSelectedMonth(null);
      },
      customImage: {
        materialIcon: <Visibility style={{ color: theme.palette.primary.main, fontSize: '20px' }} />,
        buttonText: strings.rfq_btn_view_details,
        handleCustomImageMethod,
        privilege: privileges.edit_rfq,
      },
      onRowClick: (item: any) => {
        if (item?.id != null) navigate(`${routes.viewRfq}/${item.id}`);
      },
      tableColumn: [
        {
          field: 'customerName',
          headerName: 'Customer',
          flex: 1.6,
          sortable: true,
          localField: 'firstName',
          renderCell: (_value: any, row: any) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.25 }}>
              <Avatar sx={getTableAvatarSx(row.customerName)}>
                {getInitials(row.customerName)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, lineHeight: 1.35, color: '#101828' }}>
                  {row.customerName || '—'}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.35 }}>
                  {row.eventTypeName || '—'}
                </Typography>
              </Box>
            </Box>
          ),
        },
        {
          field: 'venueName',
          headerName: strings.rfq_column_venue,
          flex: 1.2,
          sortable: true,
          localField: 'venueId',
          renderCell: (_value: any, row: any) => (
            <DualLineCell
              primary={row.venueName || '—'}
              secondary={
                row.guests != null && Number(row.guests) > 0
                  ? `${Number(row.guests).toLocaleString()} Guests`
                  : undefined
              }
            />
          ),
        },
        {
          field: 'preferredStartDate',
          headerName: 'Event',
          flex: 1.1,
          sortable: true,
          renderCell: (_value: any, row: any) => (
            <DualLineCell
              primary={formatDateLine(row.preferredStartDate)}
              secondary={formatWeekdayTime(row.preferredStartDate, row.startTime)}
            />
          ),
        },
        {
          field: 'email',
          headerName: 'Contact',
          flex: 1.3,
          sortable: true,
          renderCell: (_value: any, row: any) => {
            const email = row.email?.trim();
            const phone = row.mobile?.trim();
            if (!email && !phone) return '—';
            return <DualLineCell primary={email || '—'} secondary={phone || undefined} />;
          },
        },
        {
          field: 'createdAt',
          headerName: 'Received On',
          flex: 1.1,
          sortable: true,
          renderCell: (_value: any, row: any) => (
            <DualLineCell primary={formatDateLine(row.createdAt)} secondary={formatRelativeReceived(row.createdAt)} />
          ),
        },
        {
          field: 'status',
          headerName: strings.rfq_column_status,
          flex: 0.9,
          sortable: true,
          renderCell: (value: any) => (
            <Chip
              label={String(value || '')
                .toLowerCase()
                .replace('_', ' ')}
              size="small"
              sx={{ textTransform: 'capitalize', fontWeight: 500, ...getChipStyles(String(value || '')) }}
            />
          ),
        },
      ],
    }),
    [
      getChipStyles,
      handleCustomImageMethod,
      navigate,
      rfqPointers,
      rfqStatusOptions,
      selectedMonth,
      selectedStatus,
      theme.palette.primary.main,
    ],
  );

  const handleCatch = (error: any) => {
    setNotification({
      display: true,
      severity: 'error',
      message: (error.response.data?.error_description ?? error.response.data.message) ?? error.message,
    });
  };

  const getList = useCallback(
    async (filterData: PageProps) => {
      try {
        setIsLoading(true);
        const requestBody = filterData
          ? { ...filterData, filter: withRfqFilters(filterData.filter) }
          : {
              page: index + 1,
              size: pageSize,
              filter: withRfqFilters(filter),
              sort: ['createdAt:desc'],
            };

        const { data } = await axiosInstance.post(pageConfig.listApi, requestBody);
        setPageResponse(data);
      } catch (error: any) {
        handleCatch(error);
      } finally {
        setIsLoading(false);
      }
    },
    [filter, index, pageSize, pageConfig.listApi, withRfqFilters],
  );

  const getMasterData = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await axiosInstance.get(URLS.getEventAndVenuesRfq);
      setRfqStatusOptions(createDropdownData(data?.statuses, ['id', 'name']));
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getRFQPointers = useCallback(async (fromDate: string, toDate: string) => {
    try {
      setIsLoading(true);

      const params: any = {};

      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;

      const { data } = await axiosInstance.get(URLS.getRFQPointers, { params });
      setRfqPointers(data);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    getMasterData();
  }, [getMasterData]);

  useEffect(() => {
    getList(null as any);
  }, [getList]);

  useEffect(() => {
    if (selectedMonth?.isValid()) {
      getRFQPointers(
        selectedMonth.startOf('month').format('YYYY-MM-DD'),
        selectedMonth.endOf('month').format('YYYY-MM-DD'),
      );
      return;
    }
    getRFQPointers('', '');
  }, [getRFQPointers, selectedMonth]);

  return (
    <>
      {isLoading && <PageLoader />}
      {notification.display && (
        <Notification
          isOpen={notification.display}
          message={notification.message}
          severity={notification.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}
      <ListPage isLoading={isLoading} pageConfig={pageConfig} pageResponse={pageResponse} getList={getList} />
      <RfqEntityDrawer
        open={addDrawerOpen}
        onClose={() => setAddDrawerOpen(false)}
        onSaved={() => {
          setAddDrawerOpen(false);
          getList(null as any);
          getRFQPointers(
            selectedMonth?.isValid()
              ? selectedMonth.startOf('month').format('YYYY-MM-DD')
              : '',
            selectedMonth?.isValid() ? selectedMonth.endOf('month').format('YYYY-MM-DD') : '',
          );
        }}
      />
    </>
  );
};

export default RfqList;
